import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  TextField, Button, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, TablePagination, Chip, Avatar,
  Tabs, Tab, Divider, Typography, Accordion, AccordionSummary,
  AccordionDetails, Switch, FormControlLabel, InputAdornment,
  Grid, Card, CardContent, IconButton, Tooltip,
  DialogContentText, Box, Link, Checkbox, ListItemText,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SearchIcon from "@material-ui/icons/Search";
import BlockIcon from "@material-ui/icons/Block";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import RefreshIcon from "@material-ui/icons/Refresh";
import FilterListIcon from "@material-ui/icons/FilterList";
import DescriptionIcon from "@material-ui/icons/Description";
import LayersIcon from "@material-ui/icons/Layers";
import InfoIcon from "@material-ui/icons/Info";
import EditIcon from "@material-ui/icons/Edit";
import { makeStyles } from "@material-ui/core/styles";
import { useNotify } from "react-admin";
import { getConfigGet, getConfigPost, getConfigDelete, getConfigPatch } from "../getConfig";
import ENDPOINTS, { ENDPOINTS as NAMED_ENDPOINTS } from "../apiConstants";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  filters: {
    display: "flex", gap: theme.spacing(2), flexWrap: "wrap",
    alignItems: "flex-end", marginBottom: theme.spacing(2),
  },
  field: { minWidth: 160 },
  logo: { width: 50, height: 50, objectFit: "contain" },
  loading: { display: "flex", justifyContent: "center", padding: theme.spacing(4) },
  chipEnabled: { backgroundColor: "#4caf50", color: "white" },
  chipDisabled: { backgroundColor: "#f44336", color: "white" },
  chipOpen: { backgroundColor: "#f44336", color: "white" },
  chipClosed: { backgroundColor: "#4caf50", color: "white" },
  chipHalfOpen: { backgroundColor: "#ff9800", color: "white" },
  dialogContent: { minWidth: 600 },
  formRow: { marginBottom: theme.spacing(2) },
  tabPanel: { padding: theme.spacing(2, 0) },
  sectionTitle: { fontWeight: 600, marginBottom: theme.spacing(1), marginTop: theme.spacing(1) },
  dialogField: { marginBottom: theme.spacing(2) },
  accordionRoot: { marginBottom: theme.spacing(1) },
  accordionSummary: { backgroundColor: theme.palette.grey[50] },
  jsonField: { fontFamily: "monospace" },
  dateRow: { display: "flex", gap: theme.spacing(2) },
}));

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div hidden={value !== index}>{value === index && children}</div>
);

const PROVIDERS: Record<number, string> = { 1: "Tillo", 2: "VDX", 3: "PensionBee", 4: "AppTap" };

const BLACKLIST_REASON_CODES = [
  { value: "broken_url", label: "Broken URL (auto re-enable on pass)" },
  { value: "duplicate", label: "Duplicate (never auto re-enable)" },
  { value: "bad_redirect", label: "Bad Redirect" },
  { value: "manual", label: "Manual" },
  { value: "unknown", label: "Unknown" },
];
// VDX sends discount_rate already formatted, e.g. "Up to 3.2%".
const formatRate = (rate: any) => {
  if (rate === null || rate === undefined || rate === "") return "-";
  const text = String(rate);
  return text.trim().endsWith("%") ? text : `${text}%`;
};

const tiersOf = (brand: any): any[] => {
  if (!brand?.tiers) return [];
  try {
    const parsed = typeof brand.tiers === "string" ? JSON.parse(brand.tiers) : brand.tiers;
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const enabledOnApp = (brand: any) => brand?.status === "enabled" && !brand?.is_blacklisted;

const timelinePart = (stats: any, key: "tracked" | "awarded") => {
  const part = stats?.[key];
  if (!part?.time) return null;
  return `${part.time} ${part.unit === "d" ? "days" : part.unit}`;
};

const REWARD_PAID_QUALIFIERS = [
  { value: "after purchase", label: "After Purchase" },
  { value: "after you travel", label: "After Travel" },
  { value: "after your stay", label: "After Stay" },
  { value: "after policy starts", label: "After Policy Starts" },
  { value: "after delivery", label: "After Delivery" },
  { value: "after subscription starts", label: "After Subscription Starts" },
];

const vdxStyles = makeStyles((theme) => ({
  metadataContainer: { display: "flex", justifyContent: "center", gap: "1.5em", marginBottom: "2em", flexWrap: "wrap" },
  metadataCard: { minWidth: 180, textAlign: "center" },
  metadataValue: { fontSize: "2rem", fontWeight: "bold", color: theme.palette.primary.main },
  filtersContainer: { marginBottom: "1.5em", padding: "1.5em" },
  tableContainer: { marginTop: "1em", width: "100%", overflowX: "auto" },
  table: { minWidth: 1100 },
  tableCell: { padding: "10px 14px", border: "1px solid #e0e0e0" },
  tableHeader: { backgroundColor: "#f5f5f5", fontWeight: "bold" },
  merchantLogo: { width: 40, height: 40, objectFit: "contain", borderRadius: 4 },
  chipBlacklisted: { backgroundColor: "#f44336", color: "white" },
  chipVdxEnabled: { backgroundColor: "#4caf50", color: "white" },
  chipVdxDisabled: { backgroundColor: "#9e9e9e", color: "white" },
  chipSpecialOffer: { backgroundColor: "#ff9800", color: "white", marginRight: 4 },
  chipSpeedyRewarding: { backgroundColor: "#2196f3", color: "white", marginRight: 4 },
  chipTiers: { backgroundColor: "#9c27b0", color: "white" },
  rateCell: { fontWeight: "bold", color: theme.palette.primary.main },
  trackingUrlContainer: { marginTop: "1em", padding: "1em", backgroundColor: "#f5f5f5", borderRadius: 8, wordBreak: "break-all" },
  noDataMessage: { textAlign: "center", color: theme.palette.grey[500], fontStyle: "italic", margin: "2em 0" },
  loadingOverlay: { display: "flex", justifyContent: "center", alignItems: "center", padding: "4em" },
}));

const VdxCatalogTab = () => {
  const classes = vdxStyles();
  const notify = useNotify();

  const [merchants, setMerchants] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [metadata, setMetadata] = useState({ total_brands: 0, enabled_on_app_count: 0, blacklisted_count: 0, categories: [] as any[] });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [isBlacklistedFilter, setIsBlacklistedFilter] = useState("");
  const [specialOfferFilter, setSpecialOfferFilter] = useState("");
  const [speedyRewardingFilter, setSpeedyRewardingFilter] = useState("");
  const [hasTiersFilter, setHasTiersFilter] = useState("");
  const [sortBy, setSortBy] = useState("display_name");
  const [sortOrder, setSortOrder] = useState("ASC");

  const [selected, setSelected] = useState<any>(null);
  const [blacklistOpen, setBlacklistOpen] = useState(false);
  const [unblacklistOpen, setUnblacklistOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [descOpen, setDescOpen] = useState(false);
  const [tiersOpen, setTiersOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [blacklistForm, setBlacklistForm] = useState({ reason: "", reason_code: "manual", watch: false });
  const [editForm, setEditForm] = useState({ display_name_override: "", logo_override: "", reward_paid_time: "", reward_paid_qualifier: "", blacklist_reason: "", blacklist_reason_code: "", blacklist_watch: false });

  const fetchMerchants = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page: page + 1, pageSize, sortBy, sortOrder, provider_id: 2 };
      if (search) params.search = search;
      if (isBlacklistedFilter) params.is_blacklisted = isBlacklistedFilter;
      if (specialOfferFilter) params.special_offer = specialOfferFilter;
      if (speedyRewardingFilter) params.speedy_rewarding = speedyRewardingFilter;
      if (hasTiersFilter) params.has_tiers = hasTiersFilter;
      if (categoryFilter.length > 0) params.category = categoryFilter.join(",");
      const res = await axios.get(ENDPOINTS.BRAND_CATALOG, getConfigGet(params));
      const payload = res.data as any;
      if (payload.success) {
        setMerchants(payload.data || []);
        setTotal(payload.pagination?.total || 0);
        const cats = Array.from(new Set((payload.data || []).map((merchant: any) => merchant.vdx_category).filter(Boolean)));
        const blacklisted = (payload.data || []).filter((merchant: any) => merchant.is_blacklisted).length;
        const enabled = (payload.data || []).filter((merchant: any) => merchant.status === "enabled" && !merchant.is_blacklisted).length;
        setMetadata({ total_brands: payload.pagination?.total || 0, enabled_on_app_count: enabled, blacklisted_count: blacklisted, categories: cats.map((c: any) => ({ category: c })) });
      }
    } catch (e) {
      notify((e as any)?.response?.data?.message || "Failed to load VDX merchants", "error");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, isBlacklistedFilter, specialOfferFilter, speedyRewardingFilter, hasTiersFilter, categoryFilter, sortBy, sortOrder, notify]);

  useEffect(() => { fetchMerchants(); }, [fetchMerchants]);

  const handleBlacklist = async () => {
    if (!selected || !blacklistForm.reason.trim()) { notify("Reason is required", "error"); return; }
    setActionLoading(selected.external_key);
    try {
      await axios.post(ENDPOINTS.BRAND_CATALOG, { action: "blacklist_add", brand_id: selected.id, reason: blacklistForm.reason, reason_code: blacklistForm.reason_code, watch: blacklistForm.watch }, { headers: getConfigPost() });
      notify(`${selected.display_name} blacklisted`, "info");
      setBlacklistOpen(false);
      fetchMerchants();
    } catch (e) { notify((e as any)?.response?.data?.message || "Failed to blacklist", "error"); }
    finally { setActionLoading(null); }
  };

  const handleUnblacklist = async () => {
    if (!selected?.blacklist_id) return;
    setActionLoading(selected.external_key);
    try {
      await axios.delete(`${ENDPOINTS.BRAND_CATALOG}/${selected.blacklist_id}?action=blacklist`, { headers: getConfigDelete() });
      notify(`${selected.display_name} removed from blacklist`, "info");
      setUnblacklistOpen(false);
      fetchMerchants();
    } catch (e) { notify((e as any)?.response?.data?.message || "Failed to remove from blacklist", "error"); }
    finally { setActionLoading(null); }
  };

  const handleTestBrand = async () => {
    if (!selected) return;
    setActionLoading(selected.external_key);
    setTestResult(null);
    try {
      const res = await axios.patch(`${NAMED_ENDPOINTS.VDX_BRANDS}/${selected.external_key}`, {}, { headers: getConfigPatch() });
      const payload = res.data as any;
      if (payload.status || payload.success) { setTestResult(payload.data); notify("Tracking URL generated", "info"); }
      else notify(payload.message || "Failed to test brand", "error");
    } catch (e) { notify((e as any)?.response?.data?.message || "Failed to test brand", "error"); }
    finally { setActionLoading(null); }
  };

  const handleUpdateOverrides = async () => {
    if (!selected) return;
    setActionLoading(selected.external_key);
    const payload: any = {};
    if (editForm.display_name_override.trim()) payload.display_name_override = editForm.display_name_override.trim();
    if (editForm.logo_override.trim()) payload.logo_override = editForm.logo_override.trim();
    if (editForm.reward_paid_time || editForm.reward_paid_qualifier) {
      payload.reward_paid_override = {};
      if (editForm.reward_paid_time) payload.reward_paid_override.time = parseInt(editForm.reward_paid_time, 10);
      if (editForm.reward_paid_qualifier) payload.reward_paid_override.qualifier = editForm.reward_paid_qualifier;
    }
    try {
      const hasOverrideFields = payload.display_name_override || payload.logo_override || payload.reward_paid_override;
      if (hasOverrideFields) {
        if (selected.override_id) {
          await axios.patch(`${ENDPOINTS.BRAND_CATALOG}/${selected.override_id}`, { action: "override_update", ...payload }, { headers: getConfigPatch() });
        } else {
          await axios.post(ENDPOINTS.BRAND_CATALOG, { action: "override_save", brand_id: selected.id, ...payload }, { headers: getConfigPost() });
        }
      }
      if (selected.is_blacklisted && selected.blacklist_id) {
        if (!editForm.blacklist_reason.trim()) { notify("Blacklist reason required", "error"); setActionLoading(null); return; }
        await axios.patch(`${ENDPOINTS.BRAND_CATALOG}/${selected.blacklist_id}`, { action: "blacklist_update",
          reason: editForm.blacklist_reason,
          reason_code: editForm.blacklist_reason_code || null,
          watch: editForm.blacklist_watch,
        }, { headers: getConfigPatch() });
      }
      notify("Overrides updated", "info");
      setEditOpen(false);
      fetchMerchants();
    } catch (e) { notify((e as any)?.response?.data?.message || "Failed to update overrides", "error"); }
    finally { setActionLoading(null); }
  };

  const handleClearOverrides = async () => {
    if (!selected?.override_id) return;
    setActionLoading(selected.external_key);
    try {
      await axios.delete(`${ENDPOINTS.BRAND_CATALOG}/${selected.override_id}?action=override`, { headers: getConfigDelete() });
      notify("Overrides cleared", "info");
      setEditOpen(false);
      fetchMerchants();
    } catch (e) { notify((e as any)?.response?.data?.message || "Failed to clear overrides", "error"); }
    finally { setActionLoading(null); }
  };

  const openEdit = (merchant: any) => {
    setSelected(merchant);
    setEditForm({
      display_name_override: merchant.display_name_override || "",
      logo_override: merchant.logo_override || "",
      reward_paid_time: merchant.reward_paid_override?.time?.toString() || "",
      reward_paid_qualifier: merchant.reward_paid_override?.qualifier || "",
      blacklist_reason: merchant.blacklist_reason || "",
      blacklist_reason_code: merchant.blacklist_reason_code || "",
      blacklist_watch: Boolean(merchant.blacklist_watch),
    });
    setEditOpen(true);
  };

  const resetFilters = () => { setSearchInput(""); setSearch(""); setCategoryFilter([]); setIsBlacklistedFilter(""); setSpecialOfferFilter(""); setSpeedyRewardingFilter(""); setHasTiersFilter(""); setSortBy("display_name"); setSortOrder("ASC"); setPage(0); };

  return (
    <div>
      {/* Metadata cards */}
      <div className={classes.metadataContainer}>
        {[["Total Brands", metadata.total_brands, ""], ["Enabled on App", metadata.enabled_on_app_count, "#4caf50"], ["Blacklisted", metadata.blacklisted_count, "#f44336"]].map(([label, val, color]) => (
          <Card key={String(label)} className={classes.metadataCard}>
            <CardContent>
              <Typography style={{ fontSize: "2rem", fontWeight: "bold", color: (color as string) || undefined }}>{val}</Typography>
              <Typography style={{ color: "#666" }}>{label}</Typography>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Paper className={classes.filtersContainer}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField label="Search by name or ID" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyPress={(e) => e.key === "Enter" && setSearch(searchInput)} variant="outlined" size="small" fullWidth
              InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setSearch(searchInput)} size="small"><SearchIcon /></IconButton></InputAdornment> }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select multiple value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value as string[])} label="Category"
                renderValue={(s) => (s as string[]).length === 0 ? "All" : (s as string[]).length === 1 ? (s as string[])[0] : `${(s as string[]).length} selected`}>
                {metadata.categories.map((c: any) => (
                  <MenuItem key={c.category} value={c.category}>
                    <Checkbox checked={categoryFilter.indexOf(c.category) > -1} color="primary" />
                    <ListItemText primary={c.category} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl variant="outlined" size="small" fullWidth><InputLabel>Blacklisted</InputLabel>
              <Select value={isBlacklistedFilter} onChange={(e) => setIsBlacklistedFilter(e.target.value as string)} label="Blacklisted">
                <MenuItem value="">All</MenuItem><MenuItem value="true">Blacklisted</MenuItem><MenuItem value="false">Not Blacklisted</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl variant="outlined" size="small" fullWidth><InputLabel>Special Offer</InputLabel>
              <Select value={specialOfferFilter} onChange={(e) => setSpecialOfferFilter(e.target.value as string)} label="Special Offer">
                <MenuItem value="">All</MenuItem><MenuItem value="true">Yes</MenuItem><MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl variant="outlined" size="small" fullWidth><InputLabel>Speedy Rewarding</InputLabel>
              <Select value={speedyRewardingFilter} onChange={(e) => setSpeedyRewardingFilter(e.target.value as string)} label="Speedy Rewarding">
                <MenuItem value="">All</MenuItem><MenuItem value="true">Yes</MenuItem><MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl variant="outlined" size="small" fullWidth><InputLabel>Has Tiers</InputLabel>
              <Select value={hasTiersFilter} onChange={(e) => setHasTiersFilter(e.target.value as string)} label="Has Tiers">
                <MenuItem value="">All</MenuItem><MenuItem value="true">Yes</MenuItem><MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl variant="outlined" size="small" fullWidth><InputLabel>Sort By</InputLabel>
              <Select value={sortBy} onChange={(e) => setSortBy(e.target.value as string)} label="Sort By">
                <MenuItem value="display_name">Name</MenuItem><MenuItem value="discount_rate">Rate</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl variant="outlined" size="small" fullWidth><InputLabel>Order</InputLabel>
              <Select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as string)} label="Order">
                <MenuItem value="ASC">Ascending</MenuItem><MenuItem value="DESC">Descending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}><Button variant="outlined" onClick={resetFilters} startIcon={<FilterListIcon />} fullWidth>Reset Filters</Button></Grid>
          <Grid item xs={6} md={2}><Button variant="contained" color="primary" onClick={() => fetchMerchants()} startIcon={<RefreshIcon />} fullWidth>Refresh</Button></Grid>
        </Grid>
      </Paper>

      {/* Table */}
      {loading ? (
        <div className={classes.loadingOverlay}><CircularProgress size={60} /></div>
      ) : merchants.length === 0 ? (
        <Typography className={classes.noDataMessage}>No merchants found matching your criteria</Typography>
      ) : (
        <>
          <Paper className={classes.tableContainer}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  {["Logo", "Name", "External Key", "Category", "Rate", "Status", "Features", "Actions"].map(h => (
                    <TableCell key={h} className={`${classes.tableCell} ${classes.tableHeader}`}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {merchants.map((merchant) => (
                  <TableRow key={merchant.id}>
                    <TableCell className={classes.tableCell}>{merchant.logo_url && <img src={merchant.logo_url} alt={merchant.display_name} className={classes.merchantLogo} onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />}</TableCell>
                    <TableCell className={classes.tableCell}>{merchant.display_name_override || merchant.display_name}{merchant.display_name_override && <><br /><small style={{ color: "#888" }}>{merchant.display_name}</small></>}</TableCell>
                    <TableCell className={classes.tableCell}>{merchant.external_key}</TableCell>
                    <TableCell className={classes.tableCell}>{merchant.vdx_category || "-"}</TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.rateCell}`}>{formatRate(merchant.discount_rate)}</TableCell>
                    <TableCell className={classes.tableCell}>
                      {merchant.is_blacklisted ? (
                        <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
                          <Chip label="Blacklisted" size="small" className={classes.chipBlacklisted} />
                          {merchant.blacklist_reason_code && <Chip label={BLACKLIST_REASON_CODES.find(c => c.value === merchant.blacklist_reason_code)?.label || merchant.blacklist_reason_code} size="small" variant="outlined" />}
                          {!!merchant.blacklist_watch && <Chip label="Watch" size="small" color="primary" variant="outlined" />}
                          {merchant.blacklist_reason && <Tooltip title={merchant.blacklist_reason}><Typography variant="caption" noWrap style={{ maxWidth: 140 }}>{merchant.blacklist_reason}</Typography></Tooltip>}
                        </Box>
                      ) : merchant.status === "enabled" ? (
                        <Chip label="Enabled" size="small" className={classes.chipVdxEnabled} />
                      ) : (
                        <Chip label="Disabled" size="small" className={classes.chipVdxDisabled} />
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Box display="flex" flexWrap="wrap" style={{ gap: "4px" }}>
                        {!!merchant.special_offer && <Chip label="Special Offer" size="small" className={classes.chipSpecialOffer} />}
                        {!!merchant.speedy_rewarding && <Chip label="Speedy" size="small" className={classes.chipSpeedyRewarding} />}
                        {tiersOf(merchant).length > 0 && (
                          <Chip label={`${tiersOf(merchant).length} Tiers`} size="small" className={classes.chipTiers} onClick={() => { setSelected(merchant); setTiersOpen(true); }} clickable />
                        )}
                        {merchant.description && <Chip label="Desc" size="small" onClick={() => { setSelected(merchant); setDescOpen(true); }} clickable variant="outlined" color="primary" />}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Tooltip title="View Details"><IconButton size="small" onClick={() => { setSelected(merchant); setDetailOpen(true); }}><InfoIcon /></IconButton></Tooltip>
                      <Tooltip title="Edit Overrides"><IconButton size="small" color="primary" onClick={() => openEdit(merchant)} disabled={actionLoading === merchant.external_key}><EditIcon /></IconButton></Tooltip>
                      {merchant.is_blacklisted ? (
                        <Tooltip title="Remove from Blacklist"><IconButton size="small" color="primary" onClick={() => { setSelected(merchant); setUnblacklistOpen(true); }} disabled={actionLoading === merchant.external_key}>{actionLoading === merchant.external_key ? <CircularProgress size={20} /> : <CheckCircleIcon />}</IconButton></Tooltip>
                      ) : (
                        <Tooltip title="Add to Blacklist"><IconButton size="small" color="secondary" onClick={() => { setSelected(merchant); setBlacklistForm({ reason: "", reason_code: "manual", watch: false }); setBlacklistOpen(true); }} disabled={actionLoading === merchant.external_key}>{actionLoading === merchant.external_key ? <CircularProgress size={20} /> : <BlockIcon />}</IconButton></Tooltip>
                      )}
                      <Tooltip title="Test Brand"><IconButton size="small" color="primary" onClick={() => { setSelected(merchant); setTestResult(null); setTestOpen(true); }} disabled={actionLoading === merchant.external_key}><PlayArrowIcon /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          <TablePagination component="div" count={total} page={page}
            onChangePage={(_e: unknown, p: number) => setPage(p)}
            rowsPerPage={pageSize}
            onChangeRowsPerPage={(e: React.ChangeEvent<HTMLInputElement>) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]} />
        </>
      )}

      {/* Blacklist Dialog */}
      <Dialog open={blacklistOpen} onClose={() => setBlacklistOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add to Blacklist</DialogTitle>
        <DialogContent>
          <DialogContentText>Add <strong>{selected?.display_name}</strong> to blacklist. A reason is required.</DialogContentText>
          <Box style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>
            <TextField fullWidth required multiline rows={3} label="Reason" value={blacklistForm.reason} onChange={(e) => setBlacklistForm({ ...blacklistForm, reason: e.target.value })} variant="outlined" />
            <FormControl variant="outlined" fullWidth><InputLabel>Reason Code</InputLabel>
              <Select value={blacklistForm.reason_code} onChange={(e) => setBlacklistForm({ ...blacklistForm, reason_code: e.target.value as string })} label="Reason Code">
                {BLACKLIST_REASON_CODES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControlLabel control={<Switch checked={blacklistForm.watch} onChange={(e) => setBlacklistForm({ ...blacklistForm, watch: e.target.checked })} color="primary" />} label="Watch (auto re-enable when URL test passes)" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlacklistOpen(false)}>Cancel</Button>
          <Button onClick={handleBlacklist} color="secondary" variant="contained" disabled={actionLoading !== null || !blacklistForm.reason.trim()} startIcon={<BlockIcon />}>Blacklist</Button>
        </DialogActions>
      </Dialog>

      {/* Unblacklist Dialog */}
      <Dialog open={unblacklistOpen} onClose={() => setUnblacklistOpen(false)}>
        <DialogTitle>Remove from Blacklist</DialogTitle>
        <DialogContent><DialogContentText>Remove <strong>{selected?.display_name}</strong> from blacklist?</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setUnblacklistOpen(false)}>Cancel</Button>
          <Button onClick={handleUnblacklist} color="primary" variant="contained" disabled={actionLoading !== null} startIcon={<CheckCircleIcon />}>Remove</Button>
        </DialogActions>
      </Dialog>

      {/* Test Brand Dialog */}
      <Dialog open={testOpen} onClose={() => { setTestOpen(false); setTestResult(null); }} maxWidth="md" fullWidth>
        <DialogTitle>Test Brand: {selected?.display_name}</DialogTitle>
        <DialogContent>
          <DialogContentText>Generate a tracking URL for testing this VDX brand.</DialogContentText>
          {testResult && (
            <div className={classes.trackingUrlContainer}>
              <Typography variant="subtitle2"><strong>Test User:</strong> {testResult.test_user_email}</Typography>
              <Typography variant="subtitle2"><strong>Click ID:</strong> {testResult.click_id}</Typography>
              <Typography variant="subtitle2"><strong>Tracking URL:</strong></Typography>
              <Link href={testResult.tracking_url} target="_blank" rel="noopener">{testResult.tracking_url}</Link>
              {testResult.tracking_mobile_url && (<><Typography variant="subtitle2" style={{ marginTop: "1em" }}><strong>Mobile URL:</strong></Typography><Link href={testResult.tracking_mobile_url} target="_blank" rel="noopener">{testResult.tracking_mobile_url}</Link></>)}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTestOpen(false); setTestResult(null); }}>Close</Button>
          {!testResult && <Button onClick={handleTestBrand} color="primary" variant="contained" disabled={actionLoading !== null} startIcon={actionLoading ? <CircularProgress size={20} /> : <PlayArrowIcon />}>Generate Tracking URL</Button>}
        </DialogActions>
      </Dialog>

      {/* Description Dialog */}
      <Dialog open={descOpen} onClose={() => setDescOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle><Box display="flex" alignItems="center" style={{ gap: 8 }}><DescriptionIcon color="primary" />Description: {selected?.display_name}</Box></DialogTitle>
        <DialogContent>
          {selected?.description ? <Typography variant="body1" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: selected.description }} />
            : <Typography variant="body1" color="textSecondary" style={{ fontStyle: "italic" }}>No description available.</Typography>}
        </DialogContent>
        <DialogActions><Button onClick={() => setDescOpen(false)} color="primary">Close</Button></DialogActions>
      </Dialog>

      {/* Tiers Dialog */}
      <Dialog open={tiersOpen} onClose={() => setTiersOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle><Box display="flex" alignItems="center" style={{ gap: 8 }}><LayersIcon color="primary" />Tiers: {selected?.display_name}</Box></DialogTitle>
        <DialogContent>
          {(() => {
            const tiers = selected?.tiers ? (typeof selected.tiers === "string" ? JSON.parse(selected.tiers) : selected.tiers) : [];
            return tiers.length > 0 ? (
              <Table size="small">
                <TableHead><TableRow>{["Tier Name", "Rate", "Previous Rate", "Valid From", "Valid To"].map(h => <TableCell key={h} style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>{tiers.map((t: any, i: number) => (
                  <TableRow key={t.id || i}>
                    <TableCell>{t.name}</TableCell>
                    <TableCell style={{ fontWeight: "bold", color: "#1976d2" }}>{t.rate || (t.rate_numeric ? `${parseFloat(t.rate_numeric) * 100}%` : "-")}</TableCell>
                    <TableCell style={{ color: "#888" }}>{t.rate_was || "-"}</TableCell>
                    <TableCell>{t.actual_from ? new Date(t.actual_from).toLocaleDateString() : "-"}</TableCell>
                    <TableCell>{t.actual_to ? new Date(t.actual_to).toLocaleDateString() : "Ongoing"}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            ) : <Typography color="textSecondary" style={{ fontStyle: "italic" }}>No tiers available.</Typography>;
          })()}
        </DialogContent>
        <DialogActions><Button onClick={() => setTiersOpen(false)} color="primary">Close</Button></DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            {selected?.logo_url && <img src={selected.logo_url} alt={selected.display_name} style={{ width: 48, height: 48, objectFit: "contain", borderRadius: 4 }} />}
            <Box><Typography variant="h6">{selected?.display_name}</Typography><Typography variant="caption" color="textSecondary">ID: {selected?.external_key}</Typography></Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>Basic Information</Typography>
              <Box style={{ backgroundColor: "#f9f9f9", padding: 16, borderRadius: 8 }}>
                <Typography variant="body2" gutterBottom><strong>Category:</strong> {selected?.vdx_category || "N/A"}</Typography>
                <Typography variant="body2" gutterBottom><strong>Rate:</strong> <span style={{ color: "#1976d2", fontWeight: "bold" }}>{formatRate(selected?.discount_rate)}</span></Typography>
                <Typography variant="body2" gutterBottom><strong>Status:</strong> {selected?.status || "N/A"}</Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Enabled on App:</strong>{" "}
                  <span style={{ color: enabledOnApp(selected) ? "#4caf50" : "#f44336" }}>{enabledOnApp(selected) ? "Yes" : "No"}</span>
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Blacklisted:</strong>{" "}
                  <span style={{ color: selected?.is_blacklisted ? "#f44336" : "#4caf50" }}>{selected?.is_blacklisted ? "Yes" : "No"}</span>
                </Typography>
                {!!selected?.is_blacklisted && (<>
                  <Typography variant="body2" gutterBottom><strong>Reason Code:</strong> {BLACKLIST_REASON_CODES.find(c => c.value === selected.blacklist_reason_code)?.label || selected.blacklist_reason_code || "N/A"}</Typography>
                  <Typography variant="body2" gutterBottom><strong>Watch:</strong> {selected.blacklist_watch ? "Yes" : "No"}</Typography>
                  <Typography variant="body2"><strong>Reason:</strong> {selected.blacklist_reason || "N/A"}</Typography>
                </>)}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>Features</Typography>
              <Box style={{ backgroundColor: "#f9f9f9", padding: 16, borderRadius: 8 }}>
                {[["Special Offer", selected?.special_offer], ["Speedy Rewarding", selected?.speedy_rewarding], ["Flagged", selected?.flagged]].map(([k, v]) => (
                  <Typography key={String(k)} variant="body2" gutterBottom><strong>{k}:</strong> <span style={{ color: v ? "#4caf50" : "#888" }}>{v ? "Yes" : "No"}</span></Typography>
                ))}
                <Typography variant="body2" gutterBottom>
                  <strong>Has Tiers:</strong>{" "}
                  <span style={{ color: tiersOf(selected).length ? "#7b1fa2" : "#888" }}>
                    {tiersOf(selected).length ? `Yes (${tiersOf(selected).length} tiers)` : "No"}
                  </span>
                </Typography>
              </Box>
            </Grid>
            {!!selected?.reward_paid_override && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Reward Paid Override</Typography>
                <Box style={{ backgroundColor: "#fff3e0", padding: 16, borderRadius: 8, border: "1px solid #ffb74d" }}>
                  <Typography variant="body2"><strong>Time:</strong> {(selected.reward_paid_override as any).time} days &nbsp; <strong>Qualifier:</strong> {(selected.reward_paid_override as any).qualifier}</Typography>
                </Box>
              </Grid>
            )}
            {!!selected?.purchase_timeline_stats && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Purchase Timeline</Typography>
                <Box style={{ backgroundColor: "#f9f9f9", padding: 16, borderRadius: 8 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Tracking Time:</strong> {timelinePart(selected.purchase_timeline_stats, "tracked") || "N/A"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2"><strong>Award Time:</strong> {timelinePart(selected.purchase_timeline_stats, "awarded") || "N/A"}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}
            {tiersOf(selected).length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Tiers ({tiersOf(selected).length})</Typography>
                <Table size="small" style={{ backgroundColor: "#f9f9f9" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: "bold" }}>Tier Name</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>Rate</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>Valid Period</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tiersOf(selected).map((tier: any, index: number) => (
                      <TableRow key={tier.id || index}>
                        <TableCell>{tier.name}</TableCell>
                        <TableCell style={{ fontWeight: "bold", color: "#1976d2" }}>{tier.rate || "-"}</TableCell>
                        <TableCell>
                          {tier.actual_from || tier.actual_to
                            ? `${tier.actual_from ? new Date(tier.actual_from).toLocaleDateString() : "Start"} - ${tier.actual_to ? new Date(tier.actual_to).toLocaleDateString() : "Ongoing"}`
                            : "Always"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
            )}
            {selected?.description && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Description</Typography>
                <Box style={{ backgroundColor: "#f9f9f9", padding: 16, borderRadius: 8 }}>
                  <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }} dangerouslySetInnerHTML={{ __html: selected.description }} />
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailOpen(false)} color="primary" variant="contained">Close</Button></DialogActions>
      </Dialog>

      {/* Edit Overrides Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box display="flex" alignItems="center" style={{ gap: 12 }}><EditIcon color="primary" /><Box><Typography variant="h6">Edit Overrides</Typography><Typography variant="caption" color="textSecondary">{selected?.display_name} ({selected?.external_key})</Typography></Box></Box></DialogTitle>
        <DialogContent dividers>
          <Box style={{ display: "flex", flexDirection: "column", gap: 20, paddingTop: 10 }}>
            <TextField fullWidth label="Name Override" placeholder="Leave empty to use original name" value={editForm.display_name_override} onChange={(e) => setEditForm({ ...editForm, display_name_override: e.target.value })} variant="outlined" helperText={`Original: ${selected?.display_name}`} />
            <TextField fullWidth label="Logo URL Override" placeholder="Leave empty to use original logo" value={editForm.logo_override} onChange={(e) => setEditForm({ ...editForm, logo_override: e.target.value })} variant="outlined" helperText="Enter a valid image URL" />
            <Typography variant="subtitle2" color="textSecondary">Reward Paid Override</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}><TextField fullWidth label="Time (days)" type="number" value={editForm.reward_paid_time} onChange={(e) => setEditForm({ ...editForm, reward_paid_time: e.target.value })} variant="outlined" /></Grid>
              <Grid item xs={6}>
                <FormControl variant="outlined" fullWidth><InputLabel>Qualifier</InputLabel>
                  <Select value={editForm.reward_paid_qualifier} onChange={(e) => setEditForm({ ...editForm, reward_paid_qualifier: e.target.value as string })} label="Qualifier">
                    <MenuItem value=""><em>None</em></MenuItem>
                    {REWARD_PAID_QUALIFIERS.map(q => <MenuItem key={q.value} value={q.value}>{q.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            {!!selected?.is_blacklisted && (<>
              <Typography variant="subtitle2" color="textSecondary">Blacklist Metadata</Typography>
              <TextField fullWidth required multiline rows={2} label="Blacklist Reason" value={editForm.blacklist_reason} onChange={(e) => setEditForm({ ...editForm, blacklist_reason: e.target.value })} variant="outlined" />
              <FormControl variant="outlined" fullWidth><InputLabel>Reason Code</InputLabel>
                <Select value={editForm.blacklist_reason_code} onChange={(e) => setEditForm({ ...editForm, blacklist_reason_code: e.target.value as string })} label="Reason Code">
                  <MenuItem value=""><em>None</em></MenuItem>
                  {BLACKLIST_REASON_CODES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControlLabel control={<Switch checked={editForm.blacklist_watch} onChange={(e) => setEditForm({ ...editForm, blacklist_watch: e.target.checked })} color="primary" />} label="Watch (auto re-enable on URL test pass)" />
            </>)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClearOverrides}
            color="secondary"
            disabled={actionLoading !== null || !selected?.override_id}
          >
            Clear All Overrides
          </Button>
          <Box style={{ flex: 1 }} />
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateOverrides} color="primary" variant="contained" disabled={actionLoading !== null} startIcon={actionLoading ? <CircularProgress size={20} /> : <EditIcon />}>Save Overrides</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const EMPTY_OVERRIDE = {
  display_name_override: "",
  logo_override: "",
  discount_override_percentage: "",
  discount_override_from_date: "",
  discount_override_to_date: "",
  sprive_haircut_override_percentage: "",
  sprive_haircut_override_from_date: "",
  sprive_haircut_override_to_date: "",
  boost_haircut_override_percentage: "",
  terms_override: "",
  return_policy: "",
  redemption_methods_override: "",
  redemption_instruction: "",
  exclusions: "",
  categories_override: "",
  expiry_months: "",
};

const BrandCatalogVdxMerchants = () => {
  const classes = useStyles();
  const notify = useNotify();


  const [brands, setBrands] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [providerId, setProviderId] = useState("");
  const [isBlacklisted, setIsBlacklisted] = useState("");
  const [circuitState, setCircuitState] = useState("");

  const [detailBrand, setDetailBrand] = useState<any>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [blacklistDialogOpen, setBlacklistDialogOpen] = useState(false);
  const [blacklistBrandId, setBlacklistBrandId] = useState<number | null>(null);
  const [blacklistReason, setBlacklistReason] = useState("");

  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideBrand, setOverrideBrand] = useState<any>(null);
  const [overrideForm, setOverrideForm] = useState<any>({ ...EMPTY_OVERRIDE });
  const [overrideTab, setOverrideTab] = useState(0);

  const [healthDialogOpen, setHealthDialogOpen] = useState(false);
  const [healthBrandId, setHealthBrandId] = useState<number | null>(null);
  const [newCircuitState, setNewCircuitState] = useState("closed");
  const [healthNotes, setHealthNotes] = useState("");

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const config = getConfigGet({
        page: page + 1,
        pageSize: rowsPerPage,
        ...(search && { search }),
        ...(status && { status }),
        ...(providerId && { provider_id: providerId }),
        ...(isBlacklisted !== "" && { is_blacklisted: isBlacklisted }),
        ...(circuitState && { circuit_state: circuitState }),
      });
      const res = await axios.get(ENDPOINTS.BRAND_CATALOG, config);
      const d = res.data as any;
      if (d.success) {
        setBrands(d.data || []);
        setTotal(d.pagination?.total || 0);
      }
    } catch (e) {
      notify((e as any)?.response?.data?.message || "Failed to load brand catalog", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchDetail = async (brandId: number) => {
    try {
      const res = await axios.get(`${ENDPOINTS.BRAND_CATALOG}/${brandId}`, getConfigGet({}));
      const d = res.data as any;
      setDetailBrand(d.data ?? d);
      setDetailOpen(true);
    } catch (e) {
      notify((e as any)?.response?.data?.message || "Failed to load brand detail", "error");
    }
  };

  // Filters are applied on Search, not on change, so this must not re-run when
  // they do. The ref keeps the newest fetchBrands without widening the deps.
  const fetchBrandsRef = useRef(fetchBrands);
  useEffect(() => { fetchBrandsRef.current = fetchBrands; });
  useEffect(() => { fetchBrandsRef.current(); }, [page, rowsPerPage]);

  const handleSearch = () => { setPage(0); fetchBrands(); };

  const handleBlacklist = async () => {
    if (!blacklistBrandId) return;
    try {
      await axios.post(ENDPOINTS.BRAND_CATALOG, { action: "blacklist_add", brand_id: blacklistBrandId, reason: blacklistReason }, { headers: getConfigPost() });
      notify("Brand blacklisted", "info");
      setBlacklistDialogOpen(false);
      setBlacklistReason("");
      fetchBrands();
    } catch (e) {
      notify((e as any)?.response?.data?.message || "Failed to blacklist brand", "error");
    }
  };

  const handleUnblacklist = async (brand: any) => {
    if (!brand.blacklist_id) return notify("No blacklist record found", "warning");
    try {
      await axios.delete(`${ENDPOINTS.BRAND_CATALOG}/${brand.blacklist_id}?action=blacklist`, { headers: getConfigDelete() });
      notify("Brand removed from blacklist", "info");
      fetchBrands();
    } catch (e) {
      notify((e as any)?.response?.data?.message || "Failed to remove from blacklist", "error");
    }
  };

  const openOverrideDialog = (brand: any) => {
    setOverrideBrand(brand);
    setOverrideTab(0);
    setOverrideForm({
      display_name_override: brand.display_name_override || "",
      logo_override: brand.logo_override || "",
      discount_override_percentage: brand.discount_override_percentage?.toString() || "",
      discount_override_from_date: brand.discount_override_from_date?.split("T")[0] || "",
      discount_override_to_date: brand.discount_override_to_date?.split("T")[0] || "",
      sprive_haircut_override_percentage: brand.sprive_haircut_override_percentage?.toString() || "",
      sprive_haircut_override_from_date: brand.sprive_haircut_override_from_date?.split("T")[0] || "",
      sprive_haircut_override_to_date: brand.sprive_haircut_override_to_date?.split("T")[0] || "",
      boost_haircut_override_percentage: brand.boost_haircut_override_percentage?.toString() || "",
      terms_override: brand.terms_override || "",
      return_policy: brand.return_policy || "",
      redemption_methods_override: brand.redemption_methods_override ? JSON.stringify(brand.redemption_methods_override, null, 2) : "",
      redemption_instruction: brand.redemption_instruction ? JSON.stringify(brand.redemption_instruction, null, 2) : "",
      exclusions: brand.exclusions ? JSON.stringify(brand.exclusions, null, 2) : "",
      categories_override: brand.categories_override ? JSON.stringify(brand.categories_override, null, 2) : "",
      expiry_months: brand.expiry_months?.toString() || "",
    });
    setOverrideDialogOpen(true);
  };

  const saveOverride = async () => {
    if (!overrideBrand) return;
    const parseJsonField = (val: string) => {
      if (!val || !val.trim()) return null;
      try { return JSON.parse(val); } catch { return val; }
    };
    const payload: any = {
      ...overrideForm,
      redemption_methods_override: parseJsonField(overrideForm.redemption_methods_override),
      redemption_instruction: parseJsonField(overrideForm.redemption_instruction),
      exclusions: parseJsonField(overrideForm.exclusions),
      categories_override: parseJsonField(overrideForm.categories_override),
      discount_override_percentage: overrideForm.discount_override_percentage !== "" ? parseFloat(overrideForm.discount_override_percentage) : null,
      sprive_haircut_override_percentage: overrideForm.sprive_haircut_override_percentage !== "" ? parseFloat(overrideForm.sprive_haircut_override_percentage) : null,
      boost_haircut_override_percentage: overrideForm.boost_haircut_override_percentage !== "" ? parseFloat(overrideForm.boost_haircut_override_percentage) : null,
      expiry_months: overrideForm.expiry_months !== "" ? parseInt(overrideForm.expiry_months, 10) : null,
    };
    try {
      if (overrideBrand.override_id) {
        await axios.patch(`${ENDPOINTS.BRAND_CATALOG}/${overrideBrand.override_id}`, { action: "override_update", ...payload }, { headers: getConfigPatch() });
      } else {
        await axios.post(ENDPOINTS.BRAND_CATALOG, { action: "override_save", brand_id: overrideBrand.id, ...payload }, { headers: getConfigPost() });
      }
      notify("Override saved", "info");
      setOverrideDialogOpen(false);
      fetchBrands();
    } catch (e) {
      notify((e as any)?.response?.data?.message || "Failed to save override", "error");
    }
  };

  const saveHealth = async () => {
    if (!healthBrandId) return;
    try {
      await axios.post(ENDPOINTS.BRAND_CATALOG, { action: "health_update", brand_id: healthBrandId, circuit_state: newCircuitState, notes: healthNotes || null }, { headers: getConfigPost() });
      notify(`Circuit state updated to ${newCircuitState}`, "info");
      setHealthDialogOpen(false);
      fetchBrands();
    } catch (e) {
      notify((e as any)?.response?.data?.message || "Failed to update health", "error");
    }
  };

  const setField = (key: string, value: any) => setOverrideForm((f: any) => ({ ...f, [key]: value }));

  const circuitChip = (state: string | null) => {
    if (!state || state === "closed") return <Chip label={state || "closed"} className={classes.chipClosed} size="small" />;
    if (state === "open") return <Chip label="open" className={classes.chipOpen} size="small" />;
    return <Chip label={state} className={classes.chipHalfOpen} size="small" />;
  };

  return (
    <Paper className={classes.root}>
      <h2>Brand Catalog</h2>

      <VdxCatalogTab />
      {false && <>

      {/* Filters */}
      <div className={classes.filters}>
        <TextField label="Search" value={search} onChange={(e) => setSearch(e.target.value)} className={classes.field} onKeyDown={(e) => e.key === "Enter" && handleSearch()} />
        <FormControl className={classes.field}>
          <InputLabel>Status</InputLabel>
          <Select value={status} onChange={(e) => setStatus(e.target.value as string)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="enabled">Enabled</MenuItem>
            <MenuItem value="disabled">Disabled</MenuItem>
          </Select>
        </FormControl>
        <FormControl className={classes.field}>
          <InputLabel>Provider</InputLabel>
          <Select value={providerId} onChange={(e) => setProviderId(e.target.value as string)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="1">Tillo</MenuItem>
            <MenuItem value="2">VDX</MenuItem>
            <MenuItem value="3">PensionBee</MenuItem>
            <MenuItem value="4">AppTap</MenuItem>
          </Select>
        </FormControl>
        <FormControl className={classes.field}>
          <InputLabel>Blacklisted</InputLabel>
          <Select value={isBlacklisted} onChange={(e) => setIsBlacklisted(e.target.value as string)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Blacklisted</MenuItem>
            <MenuItem value="false">Not blacklisted</MenuItem>
          </Select>
        </FormControl>
        <FormControl className={classes.field}>
          <InputLabel>Circuit State</InputLabel>
          <Select value={circuitState} onChange={(e) => setCircuitState(e.target.value as string)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
            <MenuItem value="half_open">Half Open</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" color="primary" onClick={handleSearch}>Search</Button>
        <Button variant="outlined" onClick={() => { setSearch(""); setStatus(""); setProviderId(""); setIsBlacklisted(""); setCircuitState(""); setPage(0); setTimeout(fetchBrands, 0); }}>Clear</Button>
      </div>

      {loading ? (
        <div className={classes.loading}><CircularProgress /></div>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Logo</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>External Key</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Circuit</TableCell>
                <TableCell>Blacklisted</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {brands.map((brand) => (
                <TableRow key={brand.id} hover>
                  <TableCell>{brand.id}</TableCell>
                  <TableCell>{brand.logo_url && <Avatar src={brand.logo_url} variant="square" className={classes.logo} />}</TableCell>
                  <TableCell>
                    <div>{brand.display_name_override || brand.display_name}</div>
                    {brand.display_name_override && <small style={{ color: "#888" }}>orig: {brand.display_name}</small>}
                  </TableCell>
                  <TableCell>{PROVIDERS[brand.provider_id] || brand.provider_id}</TableCell>
                  <TableCell>{brand.external_key}</TableCell>
                  <TableCell>
                    {brand.discount_override_percentage
                      ? `${brand.discount_rate + brand.discount_override_percentage}% (+${brand.discount_override_percentage}%)`
                      : `${brand.discount_rate}%`}
                  </TableCell>
                  <TableCell><Chip label={brand.status} size="small" className={brand.status === "enabled" ? classes.chipEnabled : classes.chipDisabled} /></TableCell>
                  <TableCell>{circuitChip(brand.circuit_state)}</TableCell>
                  <TableCell>
                    {brand.is_blacklisted
                      ? <Chip label="Blacklisted" size="small" className={classes.chipDisabled} />
                      : <Chip label="OK" size="small" className={classes.chipEnabled} />}
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => fetchDetail(brand.id)}>Detail</Button>
                    {brand.provider_id === 1 && <>
                      <Button size="small" onClick={() => openOverrideDialog(brand)}>Override</Button>
                      {brand.is_blacklisted
                        ? <Button size="small" color="secondary" onClick={() => handleUnblacklist(brand)}>Unblacklist</Button>
                        : <Button size="small" color="secondary" onClick={() => { setBlacklistBrandId(brand.id); setBlacklistDialogOpen(true); }}>Blacklist</Button>}
                      <Button size="small" onClick={() => { setHealthBrandId(brand.id); setNewCircuitState(brand.circuit_state || "closed"); setHealthNotes(""); setHealthDialogOpen(true); }}>Circuit</Button>
                    </>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onChangePage={(_e: unknown, p: number) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onChangeRowsPerPage={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </>
      )}

      {/* Detail dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Brand Detail — {detailBrand?.display_name}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {detailBrand && <pre style={{ fontSize: 12, overflowX: "auto" }}>{JSON.stringify(detailBrand, null, 2)}</pre>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Blacklist dialog */}
      <Dialog open={blacklistDialogOpen} onClose={() => setBlacklistDialogOpen(false)}>
        <DialogTitle>Blacklist Brand</DialogTitle>
        <DialogContent>
          <TextField label="Reason" fullWidth value={blacklistReason} onChange={(e) => setBlacklistReason(e.target.value)} className={classes.formRow} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlacklistDialogOpen(false)}>Cancel</Button>
          <Button color="secondary" onClick={handleBlacklist}>Blacklist</Button>
        </DialogActions>
      </Dialog>

      {/* Override dialog */}
      <Dialog open={overrideDialogOpen} onClose={() => setOverrideDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Override — {overrideBrand?.display_name}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Tabs value={overrideTab} onChange={(_e, v) => setOverrideTab(v)} indicatorColor="primary" textColor="primary">
            <Tab label="Identity & Discounts" />
            <Tab label="Terms & Content" />
          </Tabs>

          <TabPanel value={overrideTab} index={0}>
            <div className={classes.tabPanel}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>Brand Identity</Typography>
              <TextField className={classes.dialogField} label="Brand Name Override" variant="outlined" fullWidth size="small"
                value={overrideForm.display_name_override} onChange={(e) => setField("display_name_override", e.target.value)}
                helperText="Override the display name for this brand" />
              <TextField className={classes.dialogField} label="Brand Logo URL Override" variant="outlined" fullWidth size="small"
                value={overrideForm.logo_override} onChange={(e) => setField("logo_override", e.target.value)}
                helperText="Override the logo URL for this brand" />

              <Divider style={{ margin: "16px 0" }} />
              <Typography variant="subtitle2" className={classes.sectionTitle}>Discount Override</Typography>
              <TextField className={classes.dialogField} label="Discount Override Percentage" variant="outlined" fullWidth size="small" type="number"
                value={overrideForm.discount_override_percentage} onChange={(e) => setField("discount_override_percentage", e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                helperText="Leave empty to use base discount" />
              <div className={classes.dateRow}>
                <TextField className={classes.dialogField} label="From Date" variant="outlined" fullWidth size="small" type="date"
                  value={overrideForm.discount_override_from_date} onChange={(e) => setField("discount_override_from_date", e.target.value)}
                  InputLabelProps={{ shrink: true }} />
                <TextField className={classes.dialogField} label="To Date" variant="outlined" fullWidth size="small" type="date"
                  value={overrideForm.discount_override_to_date} onChange={(e) => setField("discount_override_to_date", e.target.value)}
                  InputLabelProps={{ shrink: true }} />
              </div>

              <Divider style={{ margin: "16px 0" }} />
              <Typography variant="subtitle2" className={classes.sectionTitle}>Sprive Haircut Override</Typography>
              <TextField className={classes.dialogField} label="Haircut Override Percentage" variant="outlined" fullWidth size="small" type="number"
                value={overrideForm.sprive_haircut_override_percentage} onChange={(e) => setField("sprive_haircut_override_percentage", e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                helperText="Leave empty to use base haircut" />
              <div className={classes.dateRow}>
                <TextField className={classes.dialogField} label="From Date" variant="outlined" fullWidth size="small" type="date"
                  value={overrideForm.sprive_haircut_override_from_date} onChange={(e) => setField("sprive_haircut_override_from_date", e.target.value)}
                  InputLabelProps={{ shrink: true }} />
                <TextField className={classes.dialogField} label="To Date" variant="outlined" fullWidth size="small" type="date"
                  value={overrideForm.sprive_haircut_override_to_date} onChange={(e) => setField("sprive_haircut_override_to_date", e.target.value)}
                  InputLabelProps={{ shrink: true }} />
              </div>

              <Divider style={{ margin: "16px 0" }} />
              <Typography variant="subtitle2" className={classes.sectionTitle}>Boost Haircut Override</Typography>
              <TextField className={classes.dialogField} label="Boost Haircut Override Percentage" variant="outlined" fullWidth size="small" type="number"
                value={overrideForm.boost_haircut_override_percentage} onChange={(e) => setField("boost_haircut_override_percentage", e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                helperText="Haircut applied when user has active boost. Leave empty for global boost haircut." />

              <Divider style={{ margin: "16px 0" }} />
              <Typography variant="subtitle2" className={classes.sectionTitle}>Other</Typography>
              <TextField className={classes.dialogField} label="Expiry Months" variant="outlined" fullWidth size="small" type="number"
                value={overrideForm.expiry_months} onChange={(e) => setField("expiry_months", e.target.value)}
                helperText="Leave empty to use default (24 months)" />
            </div>
          </TabPanel>

          <TabPanel value={overrideTab} index={1}>
            <div className={classes.tabPanel}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>Terms & Policies</Typography>
              <TextField className={classes.dialogField} label="Terms & Conditions Override" variant="outlined" fullWidth size="small" multiline rows={3}
                value={overrideForm.terms_override} onChange={(e) => setField("terms_override", e.target.value)} />
              <TextField className={classes.dialogField} label="Return Policy" variant="outlined" fullWidth size="small" multiline rows={3}
                value={overrideForm.return_policy} onChange={(e) => setField("return_policy", e.target.value)} />

              <Divider style={{ margin: "16px 0" }} />
              <Typography variant="subtitle2" className={classes.sectionTitle}>JSON Fields</Typography>

              <Accordion className={classes.accordionRoot}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                  <Typography>Redemption Methods Override</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField fullWidth multiline rows={5} variant="outlined" size="small"
                    value={overrideForm.redemption_methods_override} onChange={(e) => setField("redemption_methods_override", e.target.value)}
                    className={classes.jsonField} placeholder='{"method": "value"}' />
                </AccordionDetails>
              </Accordion>

              <Accordion className={classes.accordionRoot}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                  <Typography>Redemption Instruction</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField fullWidth multiline rows={5} variant="outlined" size="small"
                    value={overrideForm.redemption_instruction} onChange={(e) => setField("redemption_instruction", e.target.value)}
                    className={classes.jsonField} placeholder='["step1", "step2"]' />
                </AccordionDetails>
              </Accordion>

              <Accordion className={classes.accordionRoot}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                  <Typography>Exclusions</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField fullWidth multiline rows={5} variant="outlined" size="small"
                    value={overrideForm.exclusions} onChange={(e) => setField("exclusions", e.target.value)}
                    className={classes.jsonField} placeholder='["exclusion1", "exclusion2"]' />
                </AccordionDetails>
              </Accordion>

              <Accordion className={classes.accordionRoot}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                  <Typography>Categories Override</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField fullWidth multiline rows={5} variant="outlined" size="small"
                    value={overrideForm.categories_override} onChange={(e) => setField("categories_override", e.target.value)}
                    className={classes.jsonField} placeholder='["category1", "category2"]' />
                </AccordionDetails>
              </Accordion>
            </div>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)}>Cancel</Button>
          <Button color="primary" onClick={saveOverride}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Circuit state dialog */}
      <Dialog open={healthDialogOpen} onClose={() => setHealthDialogOpen(false)}>
        <DialogTitle>Update Circuit State</DialogTitle>
        <DialogContent>
          <FormControl fullWidth style={{ marginBottom: 16 }}>
            <InputLabel>Circuit State</InputLabel>
            <Select value={newCircuitState} onChange={(e) => setNewCircuitState(e.target.value as string)}>
              <MenuItem value="closed">Closed (healthy)</MenuItem>
              <MenuItem value="open">Open (broken)</MenuItem>
              <MenuItem value="half_open">Half Open (testing)</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth multiline rows={2} label="Notes (optional)" value={healthNotes} onChange={(e) => setHealthNotes(e.target.value)} variant="outlined" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setHealthDialogOpen(false); setHealthNotes(""); }}>Cancel</Button>
          <Button color="primary" onClick={saveHealth}>Update</Button>
        </DialogActions>
      </Dialog>
      </>}
    </Paper>
  );
};

export default BrandCatalogVdxMerchants;
